import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Upload, 
  Clock, 
  Save, 
  Video, 
  CheckCircle2,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import './FormsUnified.css';

const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
const ALLOWED_TYPES = ['video/mp4', 'video/webm'];

export default function HeroSectionForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [sectionId, setSectionId] = useState(40);
  const [loading, setLoading] = useState(true);
  const [submittingField, setSubmittingField] = useState({});
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  // Form values
  const [formData, setFormData] = useState({
    tagline_prefix: 'SCHOOL FOR',
    tagline_accent: 'LIFE',
    philosophy: 'FAITH, FORTITUDE AND FULFILMENT',
    video_file: ''
  });

  // Track original values to detect changes
  const [originalData, setOriginalData] = useState({
    tagline_prefix: 'SCHOOL FOR',
    tagline_accent: 'LIFE',
    philosophy: 'FAITH, FORTITUDE AND FULFILMENT',
    video_file: ''
  });

  // Track pending status per field
  const [pendingFields, setPendingFields] = useState({
    tagline_prefix: false,
    tagline_accent: false,
    philosophy: false,
    video_file: false
  });

  // Load hero section data once on mount or when apiBase changes
  const loadHeroSection = useCallback(async () => {
    setLoading(true);
    try {
      let response = await fetch(
        `${apiBase}/cms/section/hero/with-pending?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        response = await fetch(
          `${apiBase}/cms/section/40/with-pending?_t=${Date.now()}`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      if (!response.ok) {
        response = await fetch(`${apiBase}/homepage/cms/sections?published=true&_t=${Date.now()}`, {
          credentials: 'omit'
        });
      }

      if (!response.ok) {
        throw new Error(`API status ${response.status}`);
      }

      const result = await response.json();

      let section = null;
      let pending_changes = [];

      if (result.success && result.data) {
        section = result.data.section;
        pending_changes = result.data.pending_changes || [];
      } else if (result.ok && Array.isArray(result.data)) {
        section = result.data.find(s => s.section_type === 'hero' || s.id === 40);
      }

      if (section) {
        let published = section.content_published;
        if (typeof published === 'string') {
          try { published = JSON.parse(published); } catch (e) { published = {}; }
        }

        let draft = section.content;
        if (typeof draft === 'string') {
          try { draft = JSON.parse(draft); } catch (e) { draft = {}; }
        }

        const contentSource = published?.en || published?.id || draft?.en || draft?.id || published || draft || {};

        const initialValues = {
          tagline_prefix: contentSource.tagline_prefix || 'SCHOOL FOR',
          tagline_accent: contentSource.tagline_accent || 'LIFE',
          philosophy: contentSource.philosophy || 'FAITH, FORTITUDE AND FULFILMENT',
          video_file: contentSource.video_file || contentSource.video_url || ''
        };

        const pendingMap = {
          tagline_prefix: false,
          tagline_accent: false,
          philosophy: false,
          video_file: false
        };

        // Check pending approvals from section response or global approvals
        try {
          const appRes = await fetch(`${apiBase}/homepage/cms/pending-approvals?limit=50`, {
            credentials: 'include'
          });
          if (appRes.ok) {
            const appJson = await appRes.json();
            const items = appJson.data?.items || [];
            items.forEach(item => {
              if (item.section_id === section.id || item.section_type === 'hero') {
                pending_changes.push(item);
              }
            });
          }
        } catch (_) {}

        if (Array.isArray(pending_changes)) {
          pending_changes.forEach(change => {
            if (change.status === 'pending') {
              const fid = change.field_id;
              if (fid in pendingMap) {
                pendingMap[fid] = true;
                
                let val = change.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }
                if (val !== undefined && val !== null && val !== '') {
                  initialValues[fid] = val;
                }
              }
            }
          });
        }

        setFormData(initialValues);
        setOriginalData({ ...initialValues });
        setPendingFields(pendingMap);
        setSectionId(section.id || 40);
      }
    } catch (err) {
      console.warn('[HeroSectionForm] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadHeroSection();
  }, [loadHeroSection]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_VIDEO_SIZE) {
      toastRef.current?.error(`Video size exceeds 100MB limit (Selected: ${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toastRef.current?.error('Video format must be MP4 or WebM');
      return;
    }

    setVideoUploading(true);
    setVideoProgress(0);

    try {
      const data = new FormData();
      data.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setVideoProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const resp = JSON.parse(xhr.responseText);
            if (resp.success || resp.ok) {
              const filename = resp.filename || resp.url || resp.file_path;
              handleFieldChange('video_file', filename);
              toastRef.current?.success('New video uploaded. Click Save Video Change to submit.');
            } else {
              toastRef.current?.error(resp.error || 'Upload failed');
            }
          } catch (pe) {
            toastRef.current?.error('Invalid response from server');
          }
        } else {
          toastRef.current?.error(`Upload failed with status ${xhr.status}`);
        }
        setVideoUploading(false);
        setVideoProgress(0);
      });

      xhr.addEventListener('error', () => {
        toastRef.current?.error('Network error during upload');
        setVideoUploading(false);
        setVideoProgress(0);
      });

      xhr.open('POST', `${apiBase}/media/upload`);
      xhr.withCredentials = true;
      xhr.send(data);
    } catch (err) {
      toastRef.current?.error('Upload error: ' + err.message);
      setVideoUploading(false);
      setVideoProgress(0);
    }
  };

  // Submit a single field change (Single submission, no duplicates)
  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toastRef.current?.error('Section ID not found');
      return;
    }

    const currentVal = formData[fieldName];
    const originalVal = originalData[fieldName];

    if (currentVal === originalVal) {
      toastRef.current?.warning('No changes detected for this field');
      return;
    }

    setSubmittingField(prev => ({ ...prev, [fieldName]: true }));

    try {
      // Send a single submission with locale: 'en' (avoid duplicate rows in principal dashboard)
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' '),
            old_value: originalVal,
            new_value: currentVal,
            locale: 'en'
          })
        }
      );
      const lastResult = await response.json();

      if (lastResult?.success || lastResult?.ok) {
        toastRef.current?.success(`Changes for ${fieldName.replace(/_/g, ' ')} submitted for Principal review`);
        
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        setOriginalData(prev => ({
          ...prev,
          [fieldName]: currentVal
        }));
      } else {
        if (lastResult?.message?.includes('already has a pending') || lastResult?.error?.includes('PENDING_CHANGE_EXISTS')) {
          toastRef.current?.info('This field is already awaiting approval in the review queue');
        } else {
          toastRef.current?.error(lastResult?.error || lastResult?.message || 'Failed to submit changes');
        }
      }
    } catch (err) {
      toastRef.current?.error('Submit error: ' + err.message);
    } finally {
      setSubmittingField(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading hero section data...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* 1. Header & Actions (Neatly spaced, title on left, Refresh on far right) */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Video className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="unified-header-text">
            <h1 className="unified-title">Hero Section</h1>
            <p className="unified-subtitle">Manage homepage headline tagline, philosophy statement, and hero video</p>
          </div>
        </div>

        <div className="unified-header-actions">
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={loadHeroSection}
            title="Reload data from server"
          >
            <RefreshCw size={14} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* 2. Form Fields Card */}
      <div className="unified-card">
        {/* Field 1: Tagline Prefix */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Tagline Prefix</label>
              <p className="unified-field-desc">Leading text preceding the primary accent word (e.g. "A SCHOOL FOR" or "SCHOOL FOR")</p>
            </div>
            {pendingFields.tagline_prefix && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength="100"
              className="unified-input"
              value={formData.tagline_prefix}
              onChange={(e) => handleFieldChange('tagline_prefix', e.target.value)}
              disabled={pendingFields.tagline_prefix}
              placeholder="e.g. SCHOOL FOR"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('tagline_prefix')}
              disabled={
                submittingField.tagline_prefix || 
                pendingFields.tagline_prefix || 
                formData.tagline_prefix === originalData.tagline_prefix
              }
            >
              <Save size={14} /> <span>{submittingField.tagline_prefix ? 'Saving...' : 'Save'}</span>
            </button>
          </div>
        </div>

        {/* Field 2: Tagline Accent */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Tagline Accent Word</label>
              <p className="unified-field-desc">Primary keyword highlighted in gold accent on the homepage (e.g. "LIFE")</p>
            </div>
            {pendingFields.tagline_accent && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength="150"
              className="unified-input"
              value={formData.tagline_accent}
              onChange={(e) => handleFieldChange('tagline_accent', e.target.value)}
              disabled={pendingFields.tagline_accent}
              placeholder="e.g. LIFE"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('tagline_accent')}
              disabled={
                submittingField.tagline_accent || 
                pendingFields.tagline_accent || 
                formData.tagline_accent === originalData.tagline_accent
              }
            >
              <Save size={14} /> <span>{submittingField.tagline_accent ? 'Saving...' : 'Save'}</span>
            </button>
          </div>
        </div>

        {/* Field 3: Philosophy Subtitle */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Philosophy Subtitle</label>
              <p className="unified-field-desc">Core institutional philosophy and motto displayed under the main tagline</p>
            </div>
            {pendingFields.philosophy && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <textarea
              maxLength="500"
              rows={3}
              className="unified-textarea"
              value={formData.philosophy}
              onChange={(e) => handleFieldChange('philosophy', e.target.value)}
              disabled={pendingFields.philosophy}
              placeholder="e.g. FAITH, FORTITUDE AND FULFILMENT"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('philosophy')}
              disabled={
                submittingField.philosophy || 
                pendingFields.philosophy || 
                formData.philosophy === originalData.philosophy
              }
            >
              <Save size={14} /> <span>{submittingField.philosophy ? 'Saving...' : 'Save'}</span>
            </button>
          </div>
        </div>

        {/* Field 4: Hero Background Video (Upload Only, No Preview, No Filename/URL) */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Hero Background Video</label>
              <p className="unified-field-desc">Background video playing silently on the homepage behind hero text</p>
            </div>
            {pendingFields.video_file && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>

          <div className="unified-video-upload-section">
            {/* Current Video Status Indicator (Zero raw filenames or URLs) */}
            <div className="unified-video-status-bar">
              <div className="unified-video-status-info">
                {formData.video_file ? (
                  <div className="unified-video-badge unified-video-badge-active">
                    <CheckCircle2 size={15} />
                    <span>Custom Video Attached</span>
                  </div>
                ) : (
                  <div className="unified-video-badge unified-video-badge-default">
                    <Video size={15} />
                    <span>Default Video Active</span>
                  </div>
                )}
                <span className="unified-video-status-text">
                  {formData.video_file 
                    ? 'A video file is linked and configured for the homepage.' 
                    : 'The default school video is currently in use.'}
                </span>
              </div>

              {formData.video_file !== originalData.video_file && (
                <span className="unified-video-unsaved-tag">
                  <AlertCircle size={13} />
                  <span>Unsaved Upload</span>
                </span>
              )}
            </div>

            {/* Video File Upload Dropzone / Trigger */}
            <div className="unified-video-dropzone">
              <input
                id="hero-video-file-input"
                type="file"
                accept="video/mp4,video/webm"
                onChange={handleVideoUpload}
                disabled={videoUploading || pendingFields.video_file}
                style={{ display: 'none' }}
              />
              <label
                htmlFor="hero-video-file-input"
                className={`unified-video-upload-label ${videoUploading || pendingFields.video_file ? 'disabled' : ''}`}
              >
                <div className="unified-video-upload-icon-circle">
                  {videoUploading ? (
                    <RefreshCw size={22} className="spin-animate" />
                  ) : (
                    <Upload size={22} />
                  )}
                </div>
                <div className="unified-video-upload-text-block">
                  <span className="unified-video-upload-headline">
                    {videoUploading 
                      ? `Uploading Video (${videoProgress}%)...` 
                      : (formData.video_file ? 'Upload & Replace Video' : 'Upload Video File')}
                  </span>
                  <span className="unified-video-upload-guidelines">
                    MP4 or WebM format • Max 100MB • Recommended 1920x1080 resolution
                  </span>
                </div>
              </label>

              {/* Progress bar during upload */}
              {videoUploading && (
                <div className="unified-progress-track">
                  <div 
                    className="unified-progress-bar-fill" 
                    style={{ width: `${videoProgress}%` }} 
                  />
                </div>
              )}
            </div>

            {/* Save Video Action Row */}
            <div className="unified-video-action-row">
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('video_file')}
                disabled={
                  submittingField.video_file || 
                  pendingFields.video_file || 
                  formData.video_file === originalData.video_file ||
                  videoUploading
                }
              >
                <Save size={14} />
                <span>{submittingField.video_file ? 'Saving Video...' : 'Save Video Change'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
